import { AuthenticatedApiRequest } from '@goldspotdiscoveries/auth';
import { ImageEditBody, ImageObject } from '../../TypeScript/interfaces-images';
import { parseQueryParams } from '../helpers/parse-query-params';
import { RegisteredImageType } from '../../TypeScript/enums';
import { useCallback, useContext } from 'react';
import { appContext } from '../../Contexts/AppContext';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  DepthMarkerObject,
  ImageListAPIResponse,
} from '@goldspotdiscoveries/types';
import { useAppAlert } from '../../Contexts/AppAlertContext';

export const registerImage = (
  company: string,
  project: string,
  drillholeId: string,
  file: {
    fileRef: string;
    flagCreateThumbnail?: boolean;
    flagDiscardFile?: boolean;
  },
  boxNo: string,
  fromDepth: number,
  toDepth: number,
  imageType: RegisteredImageType,
  wavelength?: string,
): Promise<ImageListAPIResponse> => {
  return new Promise((resolve, reject) => {
    const body: any = {
      file,
      boxNo,
      toDepth,
      fromDepth,
    };
    if (imageType) {
      body.imageType = imageType;
    }
    if (wavelength) {
      body.wavelength = wavelength;
    }
    AuthenticatedApiRequest({
      method: 'put',
      apiName: 'Projects',
      path: `/${company}/${project}/drillholes/${drillholeId}/images`,
      postData: {
        body,
      },
    })
      .then((result: any) => {
        const response: any = result;
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export interface GetImagesListParams {
  company: string;
  project: string;
  drillholeId: string;
  query_params?: {
    next_offset_token?: number;
    imageType?: string;
    wavelength?: string;
  };
}

export const getImagesList = ({
  company,
  project,
  drillholeId,
  query_params,
}: GetImagesListParams) => {
  const validParams = ['next_offset_token', 'imageType', 'wavelength'];
  const queryParams: string = parseQueryParams(validParams, query_params || {});
  return AuthenticatedApiRequest<ImageListAPIResponse>({
    method: 'get',
    apiName: 'Projects',
    path: `/${company}/${project}/drillholes/${drillholeId}/images${queryParams}`,
  });
};

export const getImagesPath = (path: string) => {
  return AuthenticatedApiRequest<{
    result: string;
    expires_in: number;
    expires_at: string;
  }>({
    method: 'get',
    apiName: 'Files',
    path: `/${path}`,
  });
};

export const useGetImageWIthCache = () => {
  const { company, activeProject } = useContext(appContext);
  const { addAppAlert } = useAppAlert();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useCallback(
    async (key: number, path: string) => {
      const response = await queryClient.ensureQueryData({
        queryKey: ['imageURL', company, activeProject.id, key],
        queryFn: async () => {
          try {
            return getImagesPath(path);
          } catch (error) {
            addAppAlert({
              severity: 'error',
              message: t('ERROR_IMAGE_FETCH'),
            });
          }
        },
        staleTime: 3300 * 1000,
      });

      return response?.result;
    },
    [company, activeProject, queryClient],
  );
};

export const deleteImage = (
  company: string,
  project: string,
  drillholeId: string,
  imageId: number,
): Promise<{ message: string }> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'delete',
      apiName: 'Projects',
      path: `/${company}/${project}/drillholes/${drillholeId}/images/${imageId}`,
    })
      .then((result: any) => {
        if (result.error) {
          const response: { error: string; message: string } = result;
          reject(response);
        } else {
          const response: { message: string } = result;
          resolve(response);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const editImage = (
  companyId: string,
  projectId: string,
  drillholeId: string,
  imageId: number,
  body: ImageEditBody,
): Promise<{ message: string }> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'post',
      apiName: 'Projects',
      path: `/${companyId}/${projectId}/drillholes/${drillholeId}/images/${imageId}`,
      postData: { body },
    })
      .then((result: any) => {
        if (result.error) {
          const response: { error: string; message: string } = result;
          reject(response);
        } else {
          const response: { message: string } = result;
          resolve(response);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const rectifyImage = (
  companyId: string,
  projectId: string,
  drillholeId: number,
  imageId: number,
  depthMarkers: DepthMarkerObject[],
): Promise<{ message: string }> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'post',
      apiName: 'Projects',
      path: `/${companyId}/${projectId}/drillholes/${drillholeId}/images/${imageId}/rectify`,
      postData: {
        body: {
          depth_markers: depthMarkers,
        },
      },
    })
      .then((result: any) => {
        if (result.error) {
          const response: { error: string; message: string } = result;
          reject(response);
        } else {
          const response: { message: string } = result;
          resolve(response);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getProjectImages = (
  companyId: string,
  projectId: string,
  query_params: Record<string, any>,
): Promise<{ items: ImageObject[]; next_offset_token?: string }> => {
  const validParams = ['offset_token', 'limit'];
  const addedParams = { limit: 1000, ...(query_params ?? {}) };
  const queryParams: string = parseQueryParams(validParams, addedParams);
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Projects',
      path: `/${companyId}/${projectId}/images${queryParams}`,
    })
      .then((result: any) => {
        if (result.error) {
          const response: { error: string; message: string } = result;
          reject(response);
        } else {
          const response: { items: ImageObject[]; next_offset_token?: string } =
            result;
          resolve(response);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getAllProjectImages = async (
  companyId: string,
  projectId: string,
  query_params: Record<string, any> = {},
): Promise<ImageObject[]> => {
  const allProjectImages: ImageObject[] = [];
  let next_offset_token = undefined;

  do {
    const response = await getProjectImages(companyId, projectId, {
      ...query_params,
      offset_token: next_offset_token,
    });

    if (response.items) {
      allProjectImages.push(...response.items);
    }

    if (response.next_offset_token) {
      next_offset_token = response.next_offset_token;
    } else {
      next_offset_token = null;
    }
  } while (next_offset_token != null);

  console.log('All Images Length', allProjectImages.length);

  return allProjectImages;
};
